import React, { useState } from 'react';
import Intl from '@fx-ui/intl';
import classNames from 'classnames';
import {
    Button,
    CloseCircleFilled,
    Progress,
    CheckCircleFilled,
    ExclamationCircleFilled
} from '@fx-ui/fine-design';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { Upload, IconFont } from '~/components';
import { closest, get, getErrorMsg, includes } from '~/utils';
import { CONTACTS, ECode, helpSourceIntl, MAX_UPLOAD_FILE_SIZE } from '~/constants';
import { useStores } from '~/hooks';

enum IMPORT_STATE {
    NORMAL,
    SUCCESS,
    FAIL
}

interface IImportInfo {
    successCount: number;
    failCount: number;
}

interface IImportUploadPanelProps {
    className?: string;
    uploadPolicyUrl: string;
    onImport: (fileData: any) => Promise<IImportInfo | undefined>;
    onViewFailDetail: () => void; // 查看导入失败详情
}

const ImportUploadPanel = (props: IImportUploadPanelProps) => {
    const { className, uploadPolicyUrl, onImport, onViewFailDetail } = props;

    const [importState, setImportState] = useState<IMPORT_STATE>(IMPORT_STATE.NORMAL);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [errorData, setErrorData] = useState<any>({});
    const [importInfo, setImportInfo] = useState<IImportInfo>();

    const { corpUserStore } = useStores();

    const handleImportStateChange = (importState: IMPORT_STATE) => {
        setImportState(importState);
        if (importState === IMPORT_STATE.NORMAL) {
            setUploadProgress(0);
        }
    };

    const renderImportSuccess = () => {
        if (!importInfo) {
            return null;
        }
        const { failCount, successCount } = importInfo;

        let doneTip: React.ReactNode;
        let successIcon: React.ReactNode;
        if (!failCount) {
            doneTip = Intl.getHTML('fxp.base.bg.intra_org.members_imported_successfully.tip', {
                successCount
            });
            successIcon = <CheckCircleFilled className="success-icon" />;
        } else {
            doneTip = Intl.getHTML('fxp.base.bg.intra_org.members_imported_and_failed.tip', {
                successCount,
                failCount
            });
            successIcon = <ExclamationCircleFilled className="warning-icon" />;
        }
        return (
            <div className="success-wrapper">
                {successIcon}
                <div className="success-tip">{doneTip}</div>
                <div className="btn-wrapper">
                    {!!failCount && (
                        <Button
                            className="result-btn btn-view-detail"
                            type="negative"
                            onClick={() => {
                                onViewFailDetail();
                            }}
                        >
                            {Intl.get('view.fail.detail')}
                        </Button>
                    )}
                    <Button
                        className="result-btn btn-import-done"
                        type="primary"
                        onClick={() => {
                            handleImportStateChange(IMPORT_STATE.NORMAL);
                        }}
                    >
                        {Intl.get('member.import.continue')}
                    </Button>
                </div>
            </div>
        );
    };

    const renderImportFail = () => {
        if (!errorData) {
            return null;
        }

        const { code } = errorData;
        const isCorpNotVerified = code === ECode.TENANT_FEATURE_REQUIRES_AUTH_APPROVAL;
        // 邮件/短信导入超限抛特殊错
        if (includes(CONTACTS.INVITE_EXCEED_CODE, code)) {
            return (
                <div className="fail-wrapper">
                    <CloseCircleFilled className="fail-icon" />
                    <div className="fail-tip">
                        {Intl.get('invite.import.fail') + getErrorMsg(errorData)}
                    </div>
                </div>
            );
        }

        return (
            <div className="fail-wrapper">
                <CloseCircleFilled className="fail-icon" />
                <div className="fail-tip">
                    {isCorpNotVerified
                        ? Intl.get('member.invite.not.verified')
                        : getErrorMsg(errorData)}
                    {code === ECode.EXCEL_PARSED_FAILED && (
                        <Hyperlink compact underline href={helpSourceIntl.get('encrypted_excel')}>
                            {Intl.get('help.doc')}
                        </Hyperlink>
                    )}
                </div>
                {!isCorpNotVerified && (
                    <>
                        <Button
                            className="btn-import-redo"
                            type="primary"
                            onClick={() => {
                                handleImportStateChange(IMPORT_STATE.NORMAL);
                            }}
                        >
                            {Intl.get('upload.redo')}
                        </Button>
                    </>
                )}
            </div>
        );
    };

    const dealUploadSuccess = (fileData: any) => {
        onImport(fileData)
            .then((res) => {
                handleImportStateChange(IMPORT_STATE.SUCCESS);
                setImportInfo(res);
            })
            .catch((error) => {
                handleImportStateChange(IMPORT_STATE.FAIL);
                setErrorData(get(error, 'response.data', {}));
            })
            .finally(() => {
                setIsUploading(false);
                setUploadProgress(0);
            });
    };

    const renderUploadContent = () => {
        switch (importState) {
            case IMPORT_STATE.SUCCESS:
                return renderImportSuccess();
            case IMPORT_STATE.FAIL:
                return renderImportFail();
            case IMPORT_STATE.NORMAL:
                return isUploading ? (
                    <>
                        <Progress className="upload-progress" value={uploadProgress} />
                        <div className="progress-num">{Math.floor(uploadProgress)}%</div>
                    </>
                ) : (
                    <div className="upload-body">
                        <Upload
                            enableDrag
                            action=""
                            accept=".xlsx,.xls"
                            maxFileSize={MAX_UPLOAD_FILE_SIZE}
                            maxSizeTip={Intl.get('upload.maxSizeTip', { size: 5 })}
                            unSupportFileTypeTip={Intl.get('upload.notSupportFileTip')}
                            uploadFailTip={Intl.get('upload.fail.tip')}
                            uploadPolicyUrl={uploadPolicyUrl}
                            onGetUploadPolicyResult={() => ({
                                'x:uploader': corpUserStore.memberId
                            })}
                            onSuccess={(fileData) => {
                                dealUploadSuccess(fileData);
                            }}
                            onError={() => {
                                setIsUploading(false);
                                setUploadProgress(0);
                            }}
                            onBeforeUpload={() => {
                                setIsUploading(true);
                            }}
                            onProgress={(e) => {
                                setUploadProgress(e.percent);
                            }}
                            onDrop={() => setIsDragging(false)}
                            onDragLeave={() => setIsDragging(false)}
                            onDragEnter={() => setIsDragging(true)}
                        >
                            <div
                                className={classNames('file-upload-btn', {
                                    dragging: isDragging
                                })}
                                onClick={(e) => {
                                    if (!closest(e.target, '.add-btn')) {
                                        e.stopPropagation();
                                        e.preventDefault();
                                    }
                                }}
                            >
                                <IconFont className="upload-icon" type="file-upload" />
                                <span className="upload-tip1">
                                    {Intl.getHTML(
                                        'fxp.base.bg.intra_org.drag_or_click_to_add.tip',
                                        {
                                            click: (
                                                <Button className="add-btn" type="text" compact>
                                                    {Intl.get(
                                                        'fxp.base.bg.intra_org.click_to_add.btn'
                                                    )}
                                                </Button>
                                            )
                                        }
                                    )}
                                </span>
                                <span className="upload-tip2">
                                    {Intl.get('fxp.base.bg.intra_org.file_size_limit.tip', {
                                        limit: 5
                                    })}
                                </span>
                            </div>
                        </Upload>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div
            className={classNames(
                'fx-member-import-upload-panel',
                { 'has-border': importState !== IMPORT_STATE.NORMAL },
                className
            )}
        >
            {renderUploadContent()}
        </div>
    );
};

export default ImportUploadPanel;
